import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@agms/ui'
import { useApplicationBoardingReadiness } from '../../hooks/useApplication'
import type { BoardingReadinessGroup } from '../../lib/api'

interface Props {
  applicationId: string
}

const STATUS_LABELS = {
  ready: 'Ready to Board',
  needs_attention: 'Needs Attention',
  blocked: 'Blocked',
}

const STATUS_CLASSES = {
  ready: 'bg-green-50 text-green-800 border-green-200',
  needs_attention: 'bg-amber-50 text-amber-800 border-amber-200',
  blocked: 'bg-red-50 text-red-800 border-red-200',
}

const GROUP_CLASSES: Record<BoardingReadinessGroup['severity'], string> = {
  error: 'border-red-200 bg-red-50',
  warning: 'border-amber-200 bg-amber-50',
  info: 'border-blue-200 bg-blue-50',
}

const DOT_CLASSES: Record<BoardingReadinessGroup['severity'], string> = {
  error: 'bg-red-500',
  warning: 'bg-amber-500',
  info: 'bg-blue-500',
}

export function BoardingReadinessPanel({ applicationId }: Props) {
  const { data, isLoading, error } = useApplicationBoardingReadiness(applicationId)
  const [showDetails, setShowDetails] = useState(false)
  const actionableCount = data ? data.summary.error + data.summary.warning : 0

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle>Boarding Status</CardTitle>
            {data && (
              <p className="mt-1 text-sm text-gray-500">
                {data.status === 'ready'
                  ? 'No boarding blockers detected.'
                  : `${actionableCount} item${actionableCount === 1 ? '' : 's'} need attention before boarding.`}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {data && (
              <span className={`rounded border px-2 py-1 text-xs font-semibold ${STATUS_CLASSES[data.status]}`}>
                {STATUS_LABELS[data.status]}
              </span>
            )}
            {data && data.groups.length > 0 && (
              <button
                type="button"
                onClick={() => setShowDetails((current) => !current)}
                className="rounded border border-gray-200 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
              >
                {showDetails ? 'Hide details' : 'Details'}
              </button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="space-y-2">
            <div className="h-4 w-40 animate-pulse rounded bg-gray-100" />
            <div className="h-16 animate-pulse rounded bg-gray-100" />
          </div>
        )}

        {error && (
          <p className="text-sm text-red-700">
            Boarding readiness could not be loaded.
          </p>
        )}

        {data && (
          <div className="space-y-4">
            {data.groups.length === 0 ? (
              <p className="text-sm text-green-700">
                This application has the fields and attachments needed for the current SubmitMPA path.
              </p>
            ) : showDetails ? (
              <div className="space-y-3">
                <div className="grid grid-cols-4 gap-2 text-center">
                  <Metric label="Total" value={data.summary.total} />
                  <Metric label="Blocked" value={data.summary.error} />
                  <Metric label="Warnings" value={data.summary.warning} />
                  <Metric label="Notes" value={data.summary.info} />
                </div>
                {data.groups.map((group) => (
                  <section
                    key={group.key}
                    className={`rounded-md border p-3 ${GROUP_CLASSES[group.severity]}`}
                  >
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className={`h-2 w-2 shrink-0 rounded-full ${DOT_CLASSES[group.severity]}`} />
                        <h4 className="truncate text-sm font-semibold text-gray-900">{group.label}</h4>
                      </div>
                      <span className="shrink-0 text-xs font-medium text-gray-600">{group.items.length}</span>
                    </div>
                    <ul className="space-y-2">
                      {group.items.slice(0, 8).map((item) => (
                        <li key={item.code} className="rounded border border-white/70 bg-white/70 p-2">
                          <div className="flex items-start justify-between gap-3">
                            <span className="text-sm font-medium text-gray-900">{item.label}</span>
                            <span className="shrink-0 text-xs text-gray-500">{item.location}</span>
                          </div>
                          <p className="mt-1 text-xs leading-5 text-gray-600">{item.action}</p>
                        </li>
                      ))}
                    </ul>
                    {group.items.length > 8 && (
                      <p className="mt-2 text-xs font-medium text-gray-600">
                        {group.items.length - 8} more items in this group.
                      </p>
                    )}
                  </section>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-600">
                Open details only when reviewing a blocked submission or investigating a Fiserv response.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-gray-100 bg-gray-50 px-2 py-2">
      <div className="text-base font-semibold text-gray-900">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  )
}
